import { useEffect, useRef } from "react";
import { MessageSquare } from "lucide-react";

/**
 * XConversation — read-only embed of curated mainstream news accounts on X.
 *
 * Uses Twitter/X's official widgets.js (no API key required). Shows a
 * compact timeline of free, public posts from accounts we trust to stay
 * civil — wire services and major newspaper accounts only. No firebrands.
 *
 * Free to embed. No backend cost. Falls back silently if widgets.js is
 * blocked by the reader's browser.
 */
const CURATED_LISTS = [
  // (label, X handle, list-id or null) — we use a screen_name timeline
  // because lists require auth. screen_name shows the account's own posts.
  { label: "AP", handle: "AP" },
  { label: "Reuters", handle: "Reuters" },
  { label: "WSJ", handle: "WSJ" },
  { label: "BBC Breaking", handle: "BBCBreaking" },
  { label: "Bloomberg", handle: "business" },
  { label: "Fox News", handle: "FoxNews" },
];

export default function XConversation({ handle = "Reuters" }) {
  const ref = useRef(null);

  useEffect(() => {
    // Lazy-load X widgets.js once
    if (!document.getElementById("x-widgets-js")) {
      const s = document.createElement("script");
      s.id = "x-widgets-js";
      s.src = "https://platform.twitter.com/widgets.js";
      s.async = true;
      document.head.appendChild(s);
    }
    // Rebuild when handle changes
    if (window.twttr && window.twttr.widgets && ref.current) {
      try { window.twttr.widgets.load(ref.current); } catch { /* noop */ }
    }
  }, [handle]);

  return (
    <aside className="bg-white rounded-xl border border-[var(--line)] overflow-hidden" data-testid="x-conversation">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#0e1419] text-white">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-black">
          <MessageSquare size={12} /> Live Conversation · 𝕏
        </div>
      </div>
      <div className="px-3 pt-1 pb-2 border-b border-[var(--line)] flex flex-wrap gap-1">
        {CURATED_LISTS.map((c) => (
          <a
            key={c.handle}
            href={`https://x.com/${c.handle}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            referrerPolicy="no-referrer"
            className="text-[10px] uppercase tracking-[0.12em] font-black px-2 py-1 rounded bg-[#0e1419]/5 text-[var(--ink)] hover:bg-[#0e1419] hover:text-white transition"
            data-testid={`x-account-${c.handle}`}
          >
            @{c.handle}
          </a>
        ))}
      </div>
      <div ref={ref} className="p-2 max-h-[420px] overflow-y-auto">
        <a
          className="twitter-timeline"
          data-height="380"
          data-chrome="noheader nofooter noborders transparent"
          href={`https://twitter.com/${handle}?ref_src=twsrc%5Etfw`}
        >
          Posts by @{handle}
        </a>
      </div>
      <div className="px-4 py-2 text-[10.5px] text-[var(--muted)] bg-[var(--sky)] border-t border-[var(--line)] text-center">
        Read-only embed · public posts · no Newseze account needed
      </div>
    </aside>
  );
}
